/**
 * MLeaderTextHandle - Handle for moving only the text portion of an MLeader.
 *
 * Unlike TranslationHandle which moves the entire entity,
 * this handle only moves the text location while keeping arrow tips fixed.
 * The leader lines automatically adjust to connect text to the fixed arrow tips.
 *
 * Performance optimization: Uses the ODA view.add() pattern.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { createViewGhost } from "../../entities/ghost";
import { MoveMLeaderTextCommand } from "../../commands/MoveMleaderTextCommand";
import { deleteObj } from "../../core/delete";
import { OpenMode } from "../../core/constants";
import { recordGraphicsModified } from "../../entities";

/**
 * Handle for moving only the text of an MLeader.
 * Arrow tips remain fixed, leader lines adjust automatically.
 */
export class MLeaderTextHandle extends AbstractHandle {
  readonly type = HandleType.Translation;
  readonly position = HandlePosition.Center;

  protected entityId: DrawingModule.OdDbObjectId;

  /** Entity handle string for undo/redo */
  protected entityHandle: string = "";

  /** Original text location before drag starts */
  private originalTextLocation: Point3D | null = null;

  /** Current text location during drag */
  private currentTextLocation: Point3D | null = null;

  /** Ghost entity for view-based rendering (performance optimization) */
  private ghostEntity: DrawingModule.OdDbEntity | null = null;

  /** Original anti-aliasing mode to restore after drag */
  private originalAAMode: number = 0;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityId: DrawingModule.OdDbObjectId,
    textCenter: Point3D,
  ) {
    super(viewer, history);
    this.entityId = entityId;
    this.entityHandle = this.getEntityHandle(entityId);

    // Position handle at text center
    this.worldPosition = { ...textCenter };

    // Use same styling as TranslationHandle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.handleFill,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.translation * HANDLE_SIZES.handleScale,
      icon: "move",
    };
  }

  /**
   * Start - store original text location and create ghost.
   */
  start(): void {
    if (this.isTransforming) return;
    this.isTransforming = true;

    // Get current text location from MLeader
    const mleader = this.openMLeader(OpenMode.kForRead);
    if (!mleader) {
      this.isTransforming = false;
      return;
    }

    const textLoc = new this.module.OdGePoint3d(0, 0, 0);
    mleader.getTextLocation(textLoc);
    this.originalTextLocation = { x: textLoc.x, y: textLoc.y, z: textLoc.z };
    this.currentTextLocation = { ...this.originalTextLocation };
    deleteObj(textLoc);

    // Create ghost entity for view-based rendering (performance optimization)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    if (view) {
      // PERFORMANCE: Store original AA mode and disable it for faster rendering
      this.originalAAMode = view.antiAliasingMode();
      view.enableAntiAliasing(0);

      // Try interactive mode (may not work in all WebAssembly builds)
      try {
        if (!view.isInInteractivity()) {
          view.beginInteractivity(60);
        }
      } catch {
        // beginInteractivity not supported
      }

      // Ghost only: the original stays in the view underneath, as it does for
      // every other handle. Erasing it here and re-adding it in end()/cancel()
      // left the entity unrendered — view.add() hands ODA a pointer that
      // deleteObj() then destroys on the next line, before any App.Update().
      const ghost = createViewGhost(this.module, this.viewer, this.entityId);
      if (ghost) {
        this.ghostEntity = ghost;
        view.add(ghost, null as unknown as DrawingModule.OdGsModel);
      }
    }
  }

  /**
   * Ongoing - update text location during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming || !this.originalTextLocation) return;

    // Calculate translation vector from start
    const dx = context.worldPoint.x - context.startWorldPoint.x;
    const dy = context.worldPoint.y - context.startWorldPoint.y;

    // Calculate new text location
    const newTextLocation: Point3D = {
      x: this.originalTextLocation.x + dx,
      y: this.originalTextLocation.y + dy,
      z: this.originalTextLocation.z,
    };

    // Apply to ghost MLeader (fast) or original (fallback)
    if (this.ghostEntity) {
      const ghostMLeader = this.module.OdDbMLeader.cast(this.ghostEntity);
      if (ghostMLeader) {
        const point = new this.module.OdGePoint3d(
          newTextLocation.x,
          newTextLocation.y,
          newTextLocation.z,
        );
        ghostMLeader.setTextLocation(point);
        deleteObj(point);
        // No recordGraphicsModified needed for ghosts - fast!
      }
    } else {
      // Fallback: modify original (slow path)
      this.setTextLocation(newTextLocation);
    }

    this.currentTextLocation = newTextLocation;

    // Update view with throttling for better performance
    this.viewer.throttledUpdate();
  }

  /**
   * End - create command for undo/redo.
   */
  end(): void {
    if (
      !this.isTransforming ||
      !this.originalTextLocation ||
      !this.currentTextLocation
    ) {
      this.cancel();
      return;
    }

    this.isTransforming = false;

    // Clean up ghost and restore original to view
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    }

    // Check if there was actual movement
    const dx = this.currentTextLocation.x - this.originalTextLocation.x;
    const dy = this.currentTextLocation.y - this.originalTextLocation.y;

    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
      // No significant movement - no command needed
      this.originalTextLocation = null;
      this.currentTextLocation = null;
      try {
        this.safeViewerUpdate("MLeaderTextHandle.end/no-move", {
          entityHandle: this.entityHandle,
        });
      } finally {
        if (ghostToDelete) deleteObj(ghostToDelete);
      }
      return;
    }

    // Create command for undo/redo (this will apply the change to the original)
    const command = new MoveMLeaderTextCommand(this.viewer, {
      entityHandle: this.entityHandle,
      fromLocation: this.originalTextLocation,
      toLocation: this.currentTextLocation,
    });

    this.history.execute(command);

    this.originalTextLocation = null;
    this.currentTextLocation = null;
    try {
      this.safeViewerUpdate("MLeaderTextHandle.end", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  /**
   * Cancel - restore original text location.
   */
  cancel(): void {
    if (!this.isTransforming) return;

    this.isTransforming = false;

    // Clean up ghost and restore original to view (original was never modified)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    } else if (this.originalTextLocation) {
      // Fallback: restore original position (only if no ghost was used)
      this.setTextLocation(this.originalTextLocation);
    }

    this.originalTextLocation = null;
    this.currentTextLocation = null;

    try {
      this.safeViewerUpdate("MLeaderTextHandle.cancel", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  /**
   * Open the MLeader entity.
   */
  private openMLeader(mode: number): DrawingModule.OdDbMLeader | null {
    const pObj = this.entityId.safeOpenObject(mode, false);
    const mleader = this.module.OdDbMLeader.cast(pObj);
    deleteObj(pObj);
    return mleader || null;
  }

  /**
   * Set the text location on the MLeader.
   */
  private setTextLocation(location: Point3D): void {
    const mleader = this.openMLeader(OpenMode.kForWrite);
    if (!mleader) return;

    const point = new this.module.OdGePoint3d(
      location.x,
      location.y,
      location.z,
    );
    mleader.setTextLocation(point);
    deleteObj(point);

    recordGraphicsModified(this.module, this.entityId);
    deleteObj(mleader);
  }
}
